import { NvButton, NvForm, NvModal } from '@simwood/nv-react-components';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { getAudioDevices } from '../helpers/getAudioDevices';
import {
  getInboundRinging,
  getInboundRingtoneFileName,
  inboundRingtones,
  setInboundRingtone,
} from '../helpers/ringing';
import {
  selectAudioInputDevice,
  selectAudioOutputDevice,
  selectRingingOutputDevice,
  setInboundRingingVolume,
  setVolume,
} from '../reducers/audio';
import { StoreDispatch, StoreState } from '../store';
import * as styles from '../styles/audioDeviceModal.module.scss';
import Popover from './Popover';
import Slider from './Slider';
import Pause from './icons/Hold';
import Play from './icons/Play';

const audioDeviceToNvSelectOption = (audioDevice?: {
  deviceId: string;
  groupId: string;
  kind: string;
  label: string;
}) => {
  if (!audioDevice) return undefined;
  // const label =
  //   audioDevice.deviceId === 'default' ||
  //   audioDevice.deviceId === 'communications'
  //     ? 'Default'
  //     : audioDevice.label;
  const label = audioDevice.label;
  return { label, value: audioDevice.deviceId };
};

const ringtoneToNvSelectOption = (ringtone?: {
  name: string;
  fileName: string;
}) => {
  if (!ringtone) return undefined;
  return { label: ringtone.name, value: ringtone.fileName };
};

interface Props {
  onCancel: () => void;
  onSave: () => void;
}

interface FormValues {
  audioInputDeviceId: string | undefined;
  audioOutputDeviceId: string | undefined;
  ringingOutputDeviceId: string | undefined;
  ringtone: string;
}

const AudioDeviceModal: React.FC<Props> = (props) => {
  const dispatch = useDispatch<StoreDispatch>();
  const selectedAudioInputDeviceId = useSelector(
    (state: StoreState) => state.audio.selectedAudioInputDeviceId,
  );
  const selectedAudioOutputDeviceId = useSelector(
    (state: StoreState) => state.audio.selectedAudioOutputDeviceId,
  );
  const selectedRingingOutputDeviceId = useSelector(
    (state: StoreState) => state.audio.selectedRingingOutputDeviceId,
  );
  const reduxVolume = useSelector((state: StoreState) => state.audio.volume);
  const reduxInboundRingingVolume = useSelector(
    (state: StoreState) => state.audio.inboundRingingVolume,
  );
  const [outputVolume, setOutputVolume] = useState(reduxVolume);
  const [inboundRingVolume, setInboundRingVolume] = useState(
    reduxInboundRingingVolume,
  );
  const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>(
    [],
  );
  const [audioOutputDevices, setAudioOutputDevices] = useState<
    MediaDeviceInfo[]
  >([]);

  const { handleSubmit, control, setValue } = useForm<FormValues>({
    defaultValues: {
      audioInputDeviceId: selectedAudioInputDeviceId,
      audioOutputDeviceId: selectedAudioOutputDeviceId,
      ringingOutputDeviceId: selectedRingingOutputDeviceId,
      ringtone: getInboundRingtoneFileName(),
    },
  });

  const [ringingRingtone, setRingingRingtone] = useState('');
  const [error, setError] = useState<any>(null);

  const getDevices = useCallback(() => {
    getAudioDevices({
      selectedAudioInputDeviceId,
      selectedAudioOutputDeviceId,
      selectedRingingOutputDeviceId,
    })
      .then((devices) => {
        const {
          audioInputDevices,
          audioOutputDevices,
          selectedAudioInputDevice,
          selectedAudioOutputDevice,
          selectedRingingOutputDevice,
        } = devices;

        if (audioInputDevices.length === 0) {
          setError({
            title: 'No audio input devices available.',
            message:
              'To make and receive calls please connect a microphone and then refresh this page.',
          });
          return;
        }

        if (audioOutputDevices.length === 0) {
          setError({
            title: 'No audio output devices available.',
            message:
              'To make and receive calls please connect headphones or a speaker and then refresh this page.',
          });
          return;
        }

        setAudioInputDevices(audioInputDevices);
        setAudioOutputDevices(audioOutputDevices);
        setValue('audioInputDeviceId', selectedAudioInputDevice?.deviceId);
        setValue('audioOutputDeviceId', selectedAudioOutputDevice?.deviceId);
        setValue(
          'ringingOutputDeviceId',
          selectedRingingOutputDevice?.deviceId,
        );
      })
      .catch((err) => {
        console.error('Error getting audio devices: ', err);
      });
  }, [
    selectedAudioInputDeviceId,
    selectedAudioOutputDeviceId,
    selectedRingingOutputDeviceId,
    setValue,
  ]);

  useEffect(() => {
    getDevices();

    return () => {
      getInboundRinging(true).pause();
    };
  }, [getDevices]);

  const onCancelClicked = () => {
    getInboundRinging(true).pause();
    props.onCancel();
  };

  const inputDeviceItems = audioInputDevices
    .map(audioDeviceToNvSelectOption)
    .filter((x) => !!x) as {
    label: string;
    value: string;
  }[];

  const outputDeviceItems = audioOutputDevices
    .map(audioDeviceToNvSelectOption)
    .filter((x) => !!x) as {
    label: string;
    value: string;
  }[];

  const inboundRingtoneItems = inboundRingtones
    .map(ringtoneToNvSelectOption)
    .filter((x) => !!x) as {
    label: string;
    value: string;
  }[];

  const onSaveClicked = (formValues: FormValues) => {
    if (formValues.audioInputDeviceId) {
      dispatch(selectAudioInputDevice(formValues.audioInputDeviceId));
    }
    if (formValues.audioOutputDeviceId) {
      dispatch(selectAudioOutputDevice(formValues.audioOutputDeviceId));
    }
    if (formValues.ringingOutputDeviceId) {
      dispatch(selectRingingOutputDevice(formValues.ringingOutputDeviceId));
    }

    setInboundRingtone(formValues.ringtone);

    props.onSave();
  };

  if (error && error.title) {
    return (
      <Popover title={error.title} message={error.message} type='danger' />
    );
  }

  const pauseRinging = () => {
    const inboundRinging = getInboundRinging(true);
    inboundRinging.pause();
    setRingingRingtone('');
  };

  return (
    <NvModal.Form
      size='standard'
      title='Audio Devices'
      open={true}
      setOpen={(open) => {
        if (!open) {
          onCancelClicked();
        }
      }}
      submitText='Save'
      closeOnSubmit
      onSubmit={handleSubmit(onSaveClicked)}
    >
      <NvForm.Select
        name='audioInputDeviceId'
        labelText='Microphone'
        items={inputDeviceItems}
        control={control}
      />
      <div className={styles.selectContainer}>
        <NvForm.Select
          name='audioOutputDeviceId'
          labelText='In-call audio output'
          items={outputDeviceItems}
          control={control}
        />
        <div className={styles.volumeRow}>
          {/* <label htmlFor='output-volume-slider'>Output volume</label> */}
          <div className={styles.sliderRow}>
            <Slider
              inline
              id='output-volume-slider'
              defaultValue={outputVolume}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                setOutputVolume(v);
                dispatch(setVolume(v));
              }}
              onOutsideClick={() => {}}
            />
            <span>{Math.round(outputVolume * 100)}%</span>
          </div>
        </div>
      </div>

      <div className={styles.selectContainer}>
        <NvForm.Select
          name='ringingOutputDeviceId'
          labelText='Ringing audio output'
          items={outputDeviceItems}
          control={control}
        />

        <div className={styles.volumeRow}>
          {/* <label htmlFor='inbound-ring-volume-slider'>
          Inbound ringing volume
        </label> */}
          <div className={styles.sliderRow}>
            <Slider
              inline
              id='inbound-ring-volume-slider'
              defaultValue={inboundRingVolume}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                setInboundRingVolume(v);
                dispatch(setInboundRingingVolume(v));
              }}
              onOutsideClick={() => {}}
            />
            <span>{Math.round(inboundRingVolume * 100)}%</span>
          </div>
        </div>
      </div>
      <NvForm.Select
        name='ringtone'
        labelText='Ringtone'
        items={inboundRingtoneItems}
        control={control}
        onMenuClose={() => {
          if (ringingRingtone) {
            pauseRinging();
          }
        }}
        renderItem={({ label, value }) => (
          <div className={styles.ringtoneOption}>
            <span>{label}</span>
            <NvButton
              rounded
              className={[
                styles.button,
                ringingRingtone === value
                  ? styles.pauseButton
                  : styles.playButton,
              ].join(' ')}
              inverted={ringingRingtone !== value}
              onClick={(e) => {
                e.stopPropagation();
                if (ringingRingtone === value) {
                  pauseRinging();
                } else {
                  setInboundRingtone(value!, true);
                  getInboundRinging(true).play().catch(console.warn);
                  setRingingRingtone(value!);
                }
              }}
            >
              {ringingRingtone === value ? <Pause /> : <Play />}
            </NvButton>
          </div>
        )}
      />
    </NvModal.Form>
  );
};

export default AudioDeviceModal;
